import React, { useEffect, useState } from 'react';
import "./UserDetails.css";
import uamService from '../../services/uamService';
import { useDispatch, useSelector } from 'react-redux';
import { setUsersData, deleteUser as deleteUserAction } from '../../store/slices/getUsers';
import { Loader2, ChevronLeft, ChevronRight, Plus, X, KeyRound, AlertTriangle, Edit2 } from 'lucide-react';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { downloadFile } from '../../utils/helpers';
import { downloadExcel } from '../../utils/helpers.js';
import { FaTrash } from 'react-icons/fa';

const UserAccessManagement = () => {
  const dispatch = useDispatch();
  const { users, total, pages } = useSelector((state) => state.users);
  const { role } = useSelector((state) => state.auth);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalError, setModalError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [currentLimit] = useState(10);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    designation: '',
    role: '',
    password: '',
    status: true 
  });

  const currentUser = JSON.parse(localStorage.getItem("currentUser"));

  const checkIfSelf = (targetUser) => {
    if (!currentUser || !targetUser) return false;
    return currentUser.id === targetUser.id || currentUser.userId === targetUser.id;
  };

  // Fetch Users
  const fetchUsers = async (page = currentPage) => {
    if (users.length !== 0) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await uamService.getAllUsers({ page, limit: currentLimit });
      dispatch(setUsersData(response));
    } catch (err) {
      setError(err?.message?.error || 'Failed to fetch users');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(currentPage);
  }, [currentPage]);

  // Pagination
  const totalPages = pages || 1;

  const handleNextAndPrevious = (direction) => {
    let nextPage =
      direction === "next"
        ? Math.min(currentPage + 1, totalPages)
        : Math.max(currentPage - 1, 1);

    setCurrentPage(nextPage);
  };

  const sanitizeDate = (timeDate) => {
    const d = new Date(timeDate);
    const day = String(d.getDate()).padStart(2, '0');
    const month = d.toLocaleString('en-US', { month: 'short' });
    const year = d.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const generatePassword = () => {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%&*";
    const length = Math.floor(Math.random() * 9) + 8;
    let password = "";

    for (let i = 0; i < length; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    setFormData(prev => ({ ...prev, password }));
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Close and cleanup Main Form Modal
  const closeFormModal = () => {
    setShowModal(false);
    setModalError(null);
  };

  // Close and cleanup Delete Modal
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
    setModalError(null);
  };

  // Open Create Modal
  const openCreateModal = () => {
    setIsEditMode(false);
    setEditingUserId(null);
    setModalError(null);
    setFormData({
      username: '',
      email: '',
      designation: '',
      role: '',
      password: '',
      status: true
    });
    setShowModal(true);
  };

  // Open Edit Modal
  const openEditModal = (user) => {
    setIsEditMode(true);
    setEditingUserId(user.id);
    setModalError(null);
    
    const baseUsername = user.username ? user.username.split('@')[0] : (user.name || '');
    const currentStatus = user.status === 'inactive' || user.status === false ? false : true;

    setFormData({
      username: baseUsername,
      email: user.email || '',
      designation: user.designation || '',
      role: user.role || '',
      password: '',
      status: currentStatus
    });
    setShowModal(true);
  };

  // Trigger Delete Confirmation Modal
  const openDeleteConfirmation = (userId) => {
    if (currentUser && currentUser.id === userId) {
      setModalError("Security restriction: You cannot delete your own account.");
      return;
    }
    setModalError(null);
    setUserToDelete(userId);
    setShowDeleteModal(true);
  };

  // Confirmed Delete Action
  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    try {
      setIsLoading(true);
      setModalError(null);

      await uamService.deleteUser(userToDelete); 
      dispatch(deleteUserAction(userToDelete)); 
      closeDeleteModal();
    } catch (err) {
      setModalError("Failed to delete user account.");
    } finally {
      setIsLoading(false);
    }
  };

  // Save User (Handles Create and Edit Form submission seamlessly)
  const handleSaveUser = async () => {
    try {
      setIsLoading(true);
      setModalError(null);
      
      const preparedData = { ...formData };
      const domain = currentUser?.userName?.split("@")[1] || "domain.com";
      
      if (preparedData.username && !preparedData.username.includes('@')) {
        preparedData.username = `${preparedData.username}@${domain}`;
      }

      // Track if a password update is actually happening
      const isPasswordUpdated = !!preparedData.password;

      // If password field is left empty during edit, drop it to preserve baseline server data
      if (isEditMode && !isPasswordUpdated) {
        delete preparedData.password;
      }

      if (isEditMode) {
        // 1. Fire Update API Hook
        await uamService.updateUser(editingUserId, preparedData);
        
        // 2. ONLY download the Excel sheet if the password was changed/updated
        if (isPasswordUpdated) {
          downloadExcel(preparedData);
        }
      } else {
        // 1. Fire Add API Hook (Always downloads on fresh creation since a password is required)
        await uamService.addUser(preparedData);
        
        // 2. Generate creation Excel manifest sheet
        downloadExcel(preparedData);
      }

      closeFormModal();
      setIsLoading(true); // Re-trigger loading state briefly for data refresh transparency
      const response = await uamService.getAllUsers({ page: currentPage, limit: currentLimit });
      dispatch(setUsersData(response));
    } catch (err) {
      setModalError(`Failed to ${isEditMode ? 'update' : 'add'} user account.`);
    } finally {
      setIsLoading(false);
    }
  };

  const isSuperAdmin = role === 'super_admin';
  const isAdmin = role === 'admin';
  const showActionColumn = isSuperAdmin || isAdmin;
  
  const tableColSpan = showActionColumn ? "8" : "6";
  const isEditingSelf = isEditMode && currentUser && (currentUser.id === editingUserId || currentUser.userId === editingUserId);

  return (
    <div className="uam-container">

      {/* Header */}
      <div className="uam-header">
        <div>
          <h2>User Access Management</h2>
        </div>
        {isSuperAdmin && (
          <button className="btn-add" onClick={openCreateModal}>
            <Plus size={16} />
            Add User
          </button>
        )}
      </div>

      {/* Table */}
      <div className="uam-table-card">
        <div className="uam-table-responsive">
          <table className="uam-data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Designation</th>
                <th>Role</th>
                <th>Status</th>
                <th>Created Date</th>
                {showActionColumn && <th>Edit</th>}
                {isSuperAdmin && <th>Delete</th>}
              </tr>
            </thead>

            <tbody>
              {isLoading && !showModal && !showDeleteModal ? (
                <tr>
                  <td colSpan={tableColSpan} className="uam-center">
                    <Loader2 className="spinner" />
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={tableColSpan} className="uam-center uam-error">{error}</td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={tableColSpan} className="uam-center">No users found</td>
                </tr>
              ) : (
                users.map((user) => {
                  const isSelf = checkIfSelf(user);
                  const canEdit = isSuperAdmin || (isAdmin && user.role === 'user');

                  return (
                    <tr key={user.id}>
                      <td>{user.name || user.username}</td>
                      <td>{user.email}</td>
                      <td>{user.designation}</td>
                      <td>{user.role}</td>
                      <td>
                        <span className={`status-badge ${user.status === 'inactive' || user.status === false ? 'inactive' : 'active'}`}>
                          {user.status === 'inactive' || user.status === false ? 'Inactive' : 'Active'}
                        </span>
                      </td>
                      <td>{sanitizeDate(user.created_on)}</td>
                      
                      {/* Edit Action */}
                      {showActionColumn && (
                        <td>
                          <button
                            onClick={() => openEditModal(user)}
                            className={`uam-action-edit-btn ${!canEdit ? 'disabled-action' : ''}`}
                            disabled={!canEdit}
                            title={!canEdit ? "Privilege restriction" : "Edit User Details"}
                          >
                            <Edit2 size={16} />
                          </button>
                        </td>
                      )}

                      {/* Delete Action */}
                      {isSuperAdmin && (
                        <td>
                          <button 
                            onClick={() => openDeleteConfirmation(user.id)} 
                            className={`delete-btn ${isSelf ? 'disabled-self' : ''}`}
                            title={isSelf ? "Cannot delete yourself" : "Delete User"}
                            disabled={isSelf}
                          >
                            <FaTrash size={18} />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="uam-user-pagination">
          <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>First</button>
          <button onClick={() => handleNextAndPrevious("back")} disabled={currentPage === 1}>
            <ChevronLeft size={16} />
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button onClick={() => handleNextAndPrevious("next")} disabled={currentPage === totalPages}>
            <ChevronRight size={16} />
          </button>
          <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>Last</button>
        </div>
      </div>

      {/* Create / Edit User Modal */}
      {showModal && (
        <div className="modal-overlay-uam" onClick={closeFormModal}>
          <div className="modal-container-uam" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-uam">
              <div>
                <h2>{isEditMode ? 'Modify User Profile' : 'Create New User'}</h2>
                <p>{isEditMode ? 'Update account privileges and details' : 'Fill in the details to add a new user'}</p>
              </div>
              <button className="close-btn-uam" onClick={closeFormModal}>✕</button>
            </div>

            <div className="modal-body-uam">
              {/* Inline Modal Error Banner */}
              {modalError && (
                <div className="uam-modal-error-banner">
                  <AlertTriangle size={18} />
                  <span>{modalError}</span>
                </div>
              )}

              <div className="grid-uam">
                <div className={`field-uam ${isEditMode ? 'disabled-input-field' : ''}`}>
                  <label>Username</label>
                  <input 
                    value={formData.username} 
                    onChange={(e) => handleChange('username', e.target.value)} 
                    disabled={isEditMode} 
                    className={isEditMode ? "input-disabled-styling" : ""} 
                  />
                </div>
                <div className="field-uam">
                  <label>Email</label>
                  <input value={formData.email} onChange={(e) => handleChange('email', e.target.value)} />
                </div>
              </div>

              <div className="grid-uam">
                <div className="field-uam">
                  <label>Designation</label>
                  <input value={formData.designation} onChange={(e) => handleChange('designation', e.target.value)} />
                </div>
                <div className="field-uam">
                  <label>Role</label>
                  <select 
                    value={formData.role} 
                    onChange={(e) => handleChange('role', e.target.value)} 
                    className={`select-uam ${isEditingSelf ? 'input-disabled-styling' : ''}`}
                    disabled={isEditingSelf}
                  >
                    {isEditingSelf ? (
                      <option value="super_admin">Super Admin</option>
                    ) : (
                      <>
                        <option value="">Select Role</option>
                        <option value="user">User</option>
                        {isSuperAdmin && <option value="admin">Admin</option>}
                      </>
                    )}
                  </select>
                  {isEditingSelf && <span className="uam-hint-text">Your role cannot be downgraded.</span>}
                </div>
              </div>

              {/* Status Row (Conditional on Edit Mode) */}
              {isEditMode && (
                <div className="grid-uam">
                  <div className="field-uam">
                    <label>Account Status</label>
                    <select 
                      value={formData.status} 
                      onChange={(e) => handleChange('status', e.target.value === 'true')} 
                      className={`select-uam ${isEditingSelf ? 'input-disabled-styling' : ''}`}
                      disabled={isEditingSelf} // <-- Blocks self-status manipulation
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                    {isEditingSelf && <span className="uam-hint-text">You cannot deactivate your own account.</span>}
                  </div>
                </div>
              )}

              {/* Password Segment */}
              <div className="password-box-uam">
                <div className="field-uam">
                  <label>{isEditMode ? 'New Password (Optional)' : 'Password'}</label>
                  <input 
                    value={formData.password} 
                    type="text" 
                    onChange={(e) => handleChange('password', e.target.value)}
                    placeholder={isEditMode ? "Enter new password to reset" : "Type or generate password"} 
                  />
                </div>
                <button type="button" onClick={generatePassword} className="generate-btn-uam">Generate Password</button>
              </div>
            </div>

            <div className="modal-footer-uam">
              <button className="cancel-btn-uam" onClick={closeFormModal}>Cancel</button>
              <button className="save-btn-uam" onClick={handleSaveUser} disabled={isLoading}>
                {isLoading ? <Loader2 className="spinner" size={16} /> : (isEditMode ? 'Update User' : 'Save User')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay-uam" onClick={closeDeleteModal}>
          <div className="modal-container-uam delete-modal-box" onClick={(e) => e.stopPropagation()}>
            
            <div className="modal-body-uam uam-delete-body">
              <div className="delete-icon-warning">
                <AlertTriangle size={48} />
              </div>
              <h3>Delete User Account?</h3>
              <p>Are you sure you want to delete this user? This action is permanent and cannot be undone.</p>
              
              {/* Inline Delete Modal Error */}
              {modalError && (
                <div className="uam-modal-error-banner" style={{ marginTop: '15px' }}>
                  <span>{modalError}</span>
                </div>
              )}
            </div>

            <div className="modal-footer-uam uam-delete-footer">
              <button className="cancel-btn-uam action-btn-width" onClick={closeDeleteModal} disabled={isLoading}>
                No, Keep It
              </button>
              <button className="save-btn-uam delete-confirm-btn action-btn-width" onClick={handleConfirmDelete} disabled={isLoading}>
                {isLoading ? <Loader2 className="spinner" size={16} /> : 'Yes, Delete'}
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default UserAccessManagement;